import {
  MOTION_OPTIONS,
  type MotionPreference,
  TEXT_SIZES,
  type TextSize,
  useDisplay,
} from '@app/lib/display'
import { useEffect, useId, useRef, useState } from 'react'
import '../styles/display.css'

/**
 * "Display": text size and motion for this device, next to the theme switch. A disclosure (the
 * panel opens in place, below the button); Escape closes it and returns focus to the button.
 */
export function DisplaySettings({ compact = false }: { compact?: boolean }) {
  const [prefs, set] = useDisplay()
  const [open, setOpen] = useState(false)
  const id = useId()
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = panel.current
    if (!open || !el) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setOpen(false)
      button.current?.focus()
    }
    el.addEventListener('keydown', onKey)
    return () => el.removeEventListener('keydown', onKey)
  }, [open])
  return (
    <div className={`display-settings${compact ? ' display-settings--compact' : ''}`}>
      <button
        ref={button}
        type="button"
        className="display-settings__toggle"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">Aa</span> Display
      </button>
      <div
        id={`${id}-panel`}
        ref={panel}
        className="display-settings__panel stack--sm"
        hidden={!open}
      >
        <fieldset>
          <legend>Text size</legend>
          <div className="display-settings__options">
            {TEXT_SIZES.map((o) => (
              <label key={o.value}>
                <input
                  type="radio"
                  name={`${id}-text`}
                  value={o.value}
                  checked={prefs.textSize === o.value}
                  onChange={() => set({ textSize: o.value as TextSize })}
                />{' '}
                {o.label}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>Motion</legend>
          <div className="display-settings__options">
            {MOTION_OPTIONS.map((o) => (
              <label key={o.value}>
                <input
                  type="radio"
                  name={`${id}-motion`}
                  value={o.value}
                  checked={prefs.motion === o.value}
                  onChange={() => set({ motion: o.value as MotionPreference })}
                />{' '}
                {o.label}
              </label>
            ))}
          </div>
        </fieldset>
        <p className="tiny muted mb-0">Saved on this device.</p>
      </div>
    </div>
  )
}
